// What Drive needs from the app that hosts it (#216), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createDriveModule
// from src/app/register-drive.tsx; nothing here imports the app.
//
// The generic half (modals, the session) is the shell's host contract; what is
// left is specific to this module. Every member is typed structurally, so the
// host's real objects are checked where it registers them, not here.
import type { ComponentType, ReactNode } from 'react'
import type {
    UseInfiniteQueryOptions,
    UseInfiniteQueryResult,
} from '@tanstack/react-query'
import type { Control } from 'react-hook-form'
import type { Attachment } from '@vnetwork/datacentral-core/domain'
import type {
    HostModalApi,
    HostSession,
} from '@vnetwork/datacentral-shell/host'

/** The signed-in employee, as far as Drive reads it. */
export interface DriveEmployee {
    employee_id: number
    employee_uuid: string
}

/** One file of a multi-file preview (the app's file-preview context). */
interface DriveFilePreviewItem {
    url?: string
    fileKey?: string
    downloadUrl?: string
    attachmentInfo?: Partial<Attachment>
    manualType?: boolean
    extension?: string
    isHeic?: boolean
    /** `false` = view only (no download button). Absent = allowed. */
    allowDownload?: boolean
    /**
     * Per-file rule that costs a request: asked once when the file is first
     * shown; view-only until it answers `true` (fail closed).
     */
    resolveAllowDownload?: () => Promise<boolean>
    /** Print button in the PDF viewer; hidden while the file is view-only. */
    allowPrint?: boolean
}

/**
 * The app's shared file preview (contexts/file-preview), as Drive uses it. The
 * provider is mounted once by the app's root layout and every module shares
 * it, so it stays in the app and is handed over as a hook.
 */
export interface DriveFilePreviewApi {
    preview: (
        url: string,
        downloadUrl?: string,
        attachmentInfo?: Partial<Attachment>,
        manualType?: boolean,
        options?: { allowDownload?: boolean; allowPrint?: boolean }
    ) => void
    previewMultiple: (
        files: DriveFilePreviewItem[],
        startIndex?: number
    ) => void
}

/** The props Drive gives the host's error page. */
export interface DriveErrorPageProps {
    status?: number
    error?: Error
}

/** The props Drive gives the host's employee select (share dialog). */
export interface DriveEmployeeSelectProps {
    control: Control<any>
    name: string
    multiple?: boolean
    label?: string
    excludeEmployeeIds?: number[]
}

/** The props Drive gives the host's department select (share dialog). */
export interface DriveDepartmentSelectProps {
    control: Control<any>
    name: string
    multiple?: boolean
    label?: string
}

/** The app's useInfiniteQueryWithHandler (loading / error states as a node). */
export type DriveUseInfiniteQueryWithHandler = <
    TData = unknown,
    TError = Error,
    TQueryFnData = unknown,
    TQueryKey extends readonly unknown[] = readonly unknown[],
    TPageParam = unknown,
>(
    options: UseInfiniteQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
    >
) => UseInfiniteQueryResult<TData, TError> & { renderState: () => ReactNode }

export interface DriveHost extends HostModalApi, HostSession<DriveEmployee> {
    /** The app's shared file preview dialog. */
    useFilePreview: () => DriveFilePreviewApi
    /** The app's paged-list query with standard loading and error states. */
    useInfiniteQueryWithHandler: DriveUseInfiniteQueryWithHandler
    /** The app's error page (shown inline for a shared link that fails). */
    ErrorPage: ComponentType<DriveErrorPageProps>
    /** The app's employee select (recipients of the share dialog). */
    EmployeeSelect: ComponentType<DriveEmployeeSelectProps>
    /** The app's department select (recipients of the share dialog). */
    DepartmentSelect: ComponentType<DriveDepartmentSelectProps>
}

let current: DriveHost | undefined

export function registerDriveHost(host: DriveHost): void {
    current = host
}

/** The registered host. Read it at render or call time, never at import. */
export function getDriveHost(): DriveHost {
    if (!current)
        throw new Error(
            '[drive] no host registered. Call createDriveModule({ ...hostBase, … }) at app bootstrap (src/app/register-drive.tsx).'
        )
    return current
}

// The host's hooks, under the names the app's own hooks have, so call sites
// read as before. Each reads the host at call time, inside a component.
export const useModal = () => getDriveHost().useModal()
export const useCurrentEmployee = () => getDriveHost().useCurrentEmployee()
export const useIsAdmin = () => getDriveHost().useIsAdmin()
export const useFilePreview = () => getDriveHost().useFilePreview()
export const useInfiniteQueryWithHandler: DriveUseInfiniteQueryWithHandler = (
    options
) => getDriveHost().useInfiniteQueryWithHandler(options)
